/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { formatPriceInLocale } from "@lemmonhq/orders";

export type OpenTablesBarProps = {
  busyCount: number;
  freeCount: number;
  orderCount: number;
  totalAmount: number;
  currency: string;
  loading?: boolean;
  onOpen: () => void;
};

/**
 * Single line banner above the dashboard telling the manager how much revenue
 * is still open on tables of the running shift and therefore missing from the
 * payment based numbers below.
 */
export const OpenTablesBar = defineComponent<OpenTablesBarProps>({
  name: "OpenTablesBar",
  props: [
    "busyCount",
    "freeCount",
    "orderCount",
    "totalAmount",
    "currency",
    "loading",
    "onOpen",
  ],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <button
        type="button"
        data-testid="open-tables-bar"
        onClick={() => props.onOpen()}
        title={t("reports.open_tables.bar_hint")}
        class="group flex w-full items-center gap-2 border-b border-base-300 bg-primary/10 px-2 py-1.5 text-left leading-tight transition-colors hover:bg-primary/20 xl:px-4"
      >
        <span class="relative flex size-2 shrink-0">
          <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60 motion-reduce:animate-none" />
          <span class="relative inline-flex size-2 rounded-full bg-primary" />
        </span>

        <span class="shrink-0 whitespace-nowrap text-xs font-semibold text-base-content sm:text-sm">
          {t("reports.open_tables.bar_title")}
        </span>

        {props.loading ? (
          <span class="h-3 w-24 animate-pulse rounded bg-base-content/20 motion-reduce:animate-none" />
        ) : (
          <span class="min-w-0 flex-1 truncate text-xs text-base-content/70 sm:text-sm">
            <span class="whitespace-nowrap">
              {t("reports.open_tables.busy_count", [props.busyCount])}
            </span>
            <span class="px-1 text-base-content/40">·</span>
            <span class="whitespace-nowrap">
              {t("reports.open_tables.free_count", [props.freeCount])}
            </span>
            <span class="hidden sm:inline">
              <span class="px-1 text-base-content/40">·</span>
              <span class="whitespace-nowrap">
                {t(
                  props.orderCount === 1
                    ? "reports.open_tables.orders_count_one"
                    : "reports.open_tables.orders_count",
                  [props.orderCount],
                )}
              </span>
            </span>
          </span>
        )}

        <span class="ml-auto shrink-0 whitespace-nowrap text-xs font-semibold text-accent sm:text-sm">
          {formatPriceInLocale(props.totalAmount, true, 2, props.currency)}
        </span>

        <span class="material-symbols-outlined shrink-0 text-base text-base-content/60 transition-transform group-hover:translate-x-0.5">
          chevron_right
        </span>
      </button>
    );
  },
});
